/**
 * Connectivity Context
 *
 * Purpose: Detects platform-wide backend unreachability (via periodic /health
 *          polling plus real request failures) and latches uncaught JS
 *          errors, so a single global banner can tell "the whole site is
 *          down" apart from any one component's own error state. See
 *          ORCH-285.
 *
 * Architecture: React Context + useReducer, mirroring AuthContext.tsx. The
 *              api layer calls back into this context via
 *              apiService.setNetworkFailureCallback, the same pattern
 *              AuthContext already uses for setTokenRefreshFailureCallback.
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { apiService } from '../services/api';

/** Consecutive poll/request failures required before declaring an outage. */
const FAILURE_THRESHOLD = 2;
/** How often to poll /health while the tab is visible. */
const POLL_INTERVAL_MS = 15000;

export type NetworkStatus = 'online' | 'outage';

interface ConnectivityState {
  networkStatus: NetworkStatus;
  consecutiveFailures: number;
  appError: boolean;
}

type ConnectivityAction =
  | { type: 'CONNECTIVITY_SUCCESS' }
  | { type: 'CONNECTIVITY_FAILURE' }
  | { type: 'APP_ERROR' }
  | { type: 'CLEAR_APP_ERROR' };

const initialState: ConnectivityState = {
  networkStatus: 'online',
  consecutiveFailures: 0,
  appError: false,
};

const connectivityReducer = (
  state: ConnectivityState,
  action: ConnectivityAction
): ConnectivityState => {
  switch (action.type) {
    case 'CONNECTIVITY_SUCCESS':
      return { ...state, networkStatus: 'online', consecutiveFailures: 0 };
    case 'CONNECTIVITY_FAILURE': {
      const consecutiveFailures = state.consecutiveFailures + 1;
      return {
        ...state,
        consecutiveFailures,
        networkStatus: consecutiveFailures >= FAILURE_THRESHOLD ? 'outage' : state.networkStatus,
      };
    }
    case 'APP_ERROR':
      return { ...state, appError: true };
    case 'CLEAR_APP_ERROR':
      return { ...state, appError: false };
    default:
      return state;
  }
};

interface ConnectivityContextType {
  networkStatus: NetworkStatus;
  appError: boolean;
  /** Force an immediate health check (e.g. the outage banner's "Retry now"). */
  checkNow: () => void;
  clearAppError: () => void;
}

const ConnectivityContext = createContext<ConnectivityContextType | undefined>(undefined);

/**
 * Module-level reference to the mounted provider's dispatch, so code outside
 * the React tree (index.tsx's window.onerror / unhandledrejection handlers)
 * can latch an app error the same way AuthContext registers a callback with
 * apiService.
 */
let appErrorDispatcher: (() => void) | null = null;

export const dispatchAppError = (): void => {
  appErrorDispatcher?.();
};

export const ConnectivityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(connectivityReducer, initialState);
  const inFlightRef = useRef(false);

  const runHealthCheck = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    try {
      await apiService.healthCheck();
      dispatch({ type: 'CONNECTIVITY_SUCCESS' });
    } catch {
      dispatch({ type: 'CONNECTIVITY_FAILURE' });
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    appErrorDispatcher = () => dispatch({ type: 'APP_ERROR' });
    return () => {
      appErrorDispatcher = null;
    };
  }, []);

  useEffect(() => {
    apiService.setNetworkFailureCallback(() => dispatch({ type: 'CONNECTIVITY_FAILURE' }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const clearPendingTick = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const tick = async () => {
      await runHealthCheck();
      if (!cancelled && document.visibilityState !== 'hidden') {
        timeoutId = setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    const handleVisibilityChange = () => {
      clearPendingTick();
      if (document.visibilityState === 'visible') {
        tick();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    tick();

    return () => {
      cancelled = true;
      clearPendingTick();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [runHealthCheck]);

  const clearAppError = useCallback(() => dispatch({ type: 'CLEAR_APP_ERROR' }), []);

  const value = useMemo<ConnectivityContextType>(
    () => ({
      networkStatus: state.networkStatus,
      appError: state.appError,
      checkNow: runHealthCheck,
      clearAppError,
    }),
    [state.networkStatus, state.appError, runHealthCheck, clearAppError]
  );

  return <ConnectivityContext.Provider value={value}>{children}</ConnectivityContext.Provider>;
};

export const useConnectivity = (): ConnectivityContextType => {
  const context = useContext(ConnectivityContext);
  if (context === undefined) {
    throw new Error('useConnectivity must be used within a ConnectivityProvider');
  }
  return context;
};
